import { useState, useEffect, useRef } from 'react';
import { X, Mail, Loader2, CheckCircle } from 'lucide-react';

const HUBSPOT_PORTAL_ID = '2658568';
const HUBSPOT_FORM_GUID = 'c8244408-a8cc-4a68-806f-5d4b7c56d5c8';

interface Props {
  onClose: () => void;
}

interface FormData {
  firstname: string;
  lastname: string;
  email: string;
  company: string;
}

interface FormErrors {
  firstname?: string;
  lastname?: string;
  email?: string;
}

export default function NewsletterModal({ onClose }: Props) {
  const [formData, setFormData] = useState<FormData>({
    firstname: '',
    lastname: '',
    email: '',
    company: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const modalRef = useRef<HTMLDivElement>(null);

  // Focus trap + Escape to close
  useEffect(() => {
    const firstFocusable = modalRef.current?.querySelector<HTMLElement>(
      'button:not([disabled]), input:not([disabled]), a[href]'
    );
    firstFocusable?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;
      const focusable = Array.from(
        modalRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), a[href]'
        ) ?? []
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last?.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first?.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.firstname.trim()) newErrors.firstname = 'First name is required';
    if (!formData.lastname.trim()) newErrors.lastname = 'Last name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Enter a valid email address';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isComplete =
    formData.firstname.trim() &&
    formData.lastname.trim() &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setSubmitError('');

    try {
      const res = await fetch(
        `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_GUID}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            fields: [
              { name: 'firstname', value: formData.firstname.trim() },
              { name: 'lastname',  value: formData.lastname.trim() },
              { name: 'email',     value: formData.email.trim() },
              ...(formData.company.trim()
                ? [{ name: 'company', value: formData.company.trim() }]
                : []),
            ],
            context: {
              pageUri: window.location.href,
              pageName: document.title,
            },
          }),
        }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message || `Error ${res.status}`);
      }

      setSubmitted(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      aria-hidden="true"
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />

      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="newsletter-modal-title"
        className="relative w-full max-w-md bg-white dark:bg-[#1a1a2e] rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          aria-label="Close newsletter signup"
          className="absolute top-4 right-4 z-10 p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-white bg-white/80 dark:bg-black/40 backdrop-blur-sm rounded-full transition-colors"
        >
          <X className="w-4 h-4" aria-hidden="true" />
        </button>

        {/* Header banner */}
        <div className="w-full bg-gradient-to-br from-[#635bff] to-[#00d4ff] flex items-center justify-center p-8">
          <div className="text-center">
            <Mail className="w-10 h-10 text-white/90 mx-auto mb-3" aria-hidden="true" />
            <h2 id="newsletter-modal-title" className="text-2xl font-bold text-white">Stay in the loop</h2>
            <p className="mt-1 text-sm text-white/75">
              The latest from Adventive — new formats, features, and inspiration.
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          {submitted ? (
            <div className="text-center py-4">
              <CheckCircle className="w-12 h-12 text-[#635bff] mx-auto mb-4" aria-hidden="true" />
              <h3 className="text-lg font-bold text-slate-900 dark:text-white">You're subscribed!</h3>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                Thanks for signing up. We'll be in touch soon.
              </p>
              <button
                onClick={onClose}
                className="mt-6 w-full py-2.5 px-6 text-sm font-medium text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-white/10 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
              >
                Close
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <div className="grid grid-cols-2 gap-4">
                {/* First name */}
                <div>
                  <label htmlFor="nl-firstname" className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    First Name <span className="text-red-500" aria-hidden="true">*</span>
                  </label>
                  <input
                    id="nl-firstname"
                    type="text"
                    value={formData.firstname}
                    onChange={(e) => setFormData(p => ({ ...p, firstname: e.target.value }))}
                    aria-required="true"
                    aria-describedby={errors.firstname ? 'nl-firstname-error' : undefined}
                    aria-invalid={!!errors.firstname}
                    className={`w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-white/5 text-slate-900 dark:text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#635bff]/40 transition-colors ${errors.firstname ? 'border-red-400' : 'border-slate-200 dark:border-white/10'}`}
                    placeholder="Jane"
                  />
                  {errors.firstname && <p id="nl-firstname-error" className="mt-1 text-xs text-red-500" role="alert">{errors.firstname}</p>}
                </div>

                {/* Last name */}
                <div>
                  <label htmlFor="nl-lastname" className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    Last Name <span className="text-red-500" aria-hidden="true">*</span>
                  </label>
                  <input
                    id="nl-lastname"
                    type="text"
                    value={formData.lastname}
                    onChange={(e) => setFormData(p => ({ ...p, lastname: e.target.value }))}
                    aria-required="true"
                    aria-describedby={errors.lastname ? 'nl-lastname-error' : undefined}
                    aria-invalid={!!errors.lastname}
                    className={`w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-white/5 text-slate-900 dark:text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#635bff]/40 transition-colors ${errors.lastname ? 'border-red-400' : 'border-slate-200 dark:border-white/10'}`}
                    placeholder="Smith"
                  />
                  {errors.lastname && <p id="nl-lastname-error" className="mt-1 text-xs text-red-500" role="alert">{errors.lastname}</p>}
                </div>
              </div>

              {/* Email */}
              <div className="mt-4">
                <label htmlFor="nl-email" className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                  Work Email <span className="text-red-500" aria-hidden="true">*</span>
                </label>
                <input
                  id="nl-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                  aria-required="true"
                  aria-describedby={errors.email ? 'nl-email-error' : undefined}
                  aria-invalid={!!errors.email}
                  className={`w-full px-3 py-2 text-sm border rounded-lg bg-white dark:bg-white/5 text-slate-900 dark:text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#635bff]/40 transition-colors ${errors.email ? 'border-red-400' : 'border-slate-200 dark:border-white/10'}`}
                  placeholder="jane@company.com"
                />
                {errors.email && <p id="nl-email-error" className="mt-1 text-xs text-red-500" role="alert">{errors.email}</p>}
              </div>

              {/* Company */}
              <div className="mt-4">
                <label htmlFor="nl-company" className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                  Company
                </label>
                <input
                  id="nl-company"
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData(p => ({ ...p, company: e.target.value }))}
                  className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-white/10 rounded-lg bg-white dark:bg-white/5 text-slate-900 dark:text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-[#635bff]/40 transition-colors"
                  placeholder="Acme Inc."
                />
              </div>

              {submitError && (
                <p className="mt-3 text-sm text-red-500" role="alert">{submitError}</p>
              )}

              <button
                type="submit"
                disabled={!isComplete || submitting}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 py-3 px-6 bg-[#635bff] text-white font-semibold rounded-lg hover:bg-[#5449e6] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
                    Subscribing…
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4" aria-hidden="true" />
                    Subscribe
                  </>
                )}
              </button>

              <p className="mt-3 text-center text-xs text-slate-400">
                No spam. Unsubscribe any time.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
